import React, { useEffect, useState, useRef } from "react";

import ConsumersView from "../modelViews/ConsumersView";

import { eventPointToDrawingArea } from "../modelViews/GraficElementUtils";
import Page from "../models/Page";

import PageHeader from "./PageHeader";
import Room from "../models/Room";
import { DrawingItemCategory } from "../models/DrawingItemTemplate";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import BaseModel from "../models/BaseModel";
import type Consumer from "../models/Consumer";
import { PageUsage } from "../models/PageUsage";
import Point from "../models/Point";
import type Button from "../models/Button";
import { PagePrintInfo } from "../models/PagePrintInfo";
import Logger from "../Logger";
import RoomTreeViewModel from "../modelTreeViews/RoomTreeviewModel";
import MemosView from "../modelViews/MemosView";
import ConnectableObject from "../models/ConnectableObject";
import RoomsView from "../modelViews/RoomsView";
import type Floor from "../models/Floor";
import { showToast } from "./Toasts";
import type PdfPageReference from "../models/PdfPageReference";
import RoomDialog from "../modelDialogs/RoomDialog";
import { itx } from "../translationItx/itx";
import { RenderTarget, useRenderTarget } from "./renderPrimitives/renderTarget";
import { RDiv } from "./renderPrimitives/RDiv";
import CabinetView from "../modelViews/CabinetView";
import ConnectionsView from "../modelViews/ConnectionsView";
import SceneGroupView from "../modelViews/SceneGroupView";
import { DeviceClass, DeviceClasses } from "../models/DeviceClasses";
import { EventKey } from "../eventLogger/EventKeys";

const logger = new Logger();

export function onKeyDown_ButtonOrActor(ev: any) {
  //////////////////////////////////////////////////

  // mwe tosdo:    this is a copy from menubar ====> vomomn function

  if (document.activeElement) {
    if (document.activeElement.hasAttribute("contentEditable")) {
      return;
    }

    if (document.activeElement.tagName == "INPUT") {
      return;
    }

    // logger.log("focus element: ", document.activeElement);
    // return;
  }

  // function to check the detection
  ev = ev || window.event; // Event object 'ev'
  var key = ev.which || ev.keyCode; // Detecting keyCode

  /////////////////////////////////////////////////

  if (key == 37) {
    // arrow left on keyboard is pressed
    const selectedObj = state.selectionManager.selectedObject;
    if (selectedObj) {
      ev.preventDefault();
      moveObjOnPage("left", selectedObj); // no itx
    }
  } else if (key == 39) {
    // arrow right  on keyboard is pressed
    const selectedObj = state.selectionManager.selectedObject;
    if (selectedObj) {
      ev.preventDefault();
      moveObjOnPage("right", selectedObj);
    }
  } else if (key == 38) {
    // arrow down  on keyboard is pressed
    const selectedObj = state.selectionManager.selectedObject;
    if (selectedObj) {
      ev.preventDefault();
      moveObjOnPage("down", selectedObj); // no itx
    }
  } else if (key == 40) {
    // arrow  up  on keyboard is pressed
    const selectedObj = state.selectionManager.selectedObject;
    if (selectedObj) {
      ev.preventDefault();
      moveObjOnPage("up", selectedObj); // no itx
    }
  }
}

export function moveObjOnPage(
  direction: "up" | "down" | "right" | "left", // no itx
  obj: BaseModel,
) {
  function roundTo5Up(number: number): number {
    if (number % 5 != 0) {
      number = Math.ceil(number / 5) * 5;
    }
    return number;
  }
  // move consumer/button via keyboard arrow on page
  if (obj.className == "Consumer" || obj.className == "Button") {
    const consumer = (obj as Consumer) || (obj as Button);
    var pageUsage = new PageUsage();
    const room = consumer.parent as Room;
    const pageid = room.page?.id;
    if (pageid) {
      var point = consumer.position;
      if (point) {
        const page = state.objectRegistry.getObject(pageid) as Page;
        if (page) {
          const directionMap = new Map<"up" | "down" | "right" | "left", Point>(); // no itx

          directionMap.set("up", new Point(point.x, roundTo5Up(point.y) + 5)); // no itx
          directionMap.set("down", new Point(point.x, roundTo5Up(point.y) - 5)); // no itx
          directionMap.set("left", new Point(roundTo5Up(point.x) - 5, point.y)); // no itx
          directionMap.set("right", new Point(roundTo5Up(point.x) + 5, point.y));

          directionMap.forEach((newPoint, key) => {
            if (direction == key && page.isInsideOfDrawableArea(newPoint)) {
              if (point) {
                point.x = newPoint.x;
                point.y = newPoint.y;
              }
            }
          });
        }

        pageUsage.position = point;
      }

      consumer.raiseOnChange("position");
      consumer.updateConnectionEndpoints();
    }
  }
}

export default function DrawingArea(props: { page: Page; print?: PagePrintInfo }) {
  let { print, page } = props;
  const mainPage = page;

  const target = useRenderTarget();

  const mainDrawingArea = useRef<HTMLDivElement>(null);
  const isFloorDrawing = page.isFloorPlan;

  const floor = page.parentFloor;
  if (floor && isFloorDrawing) {
    page = floor.getRoomGeneral().getOrCreatePage();
  }

  const [connectionsLoaded, setConnectionsLoaded] = useState(false);

  useOnChangeWatcher(state.uiMode);
  useOnChangeWatcher(globalThis.state.sceneGroup);
  useOnChangeWatcher(page.fixedCabinetView);
  useOnChangeWatcher(mainPage.adjustActive);

  useEffect(() => {
    if (!connectionsLoaded) {
      page.cleanUpInavalidConnections();
      page.updateConnections();
      setConnectionsLoaded(true);
    }
  });

  useEffect(() => {
    if (target === RenderTarget.web) {
      if (mainDrawingArea.current) {
        mainDrawingArea.current.addEventListener("simulatedDrop", (ev: any) => {
          logger.log("simulatedDrop received", ev.detail.data);
          if (ev.detail.data) {
            dropIntern(ev.detail, ev.detail.data);
          }
          ev.detail.data = undefined;
        });
      }
    }
  }, []);

  function onDragEnter(e: any) {
    e.preventDefault();
  }

  function onDragOver(e: any) {
    e.preventDefault();
  }

  function onDragLeave(e: any) {
    e.preventDefault();
  }

  async function dropIntern(e: any, sData: string) {
    logger.log("dropIntern", sData);
    let dropPage = page;

    function dropFunctionAllreadyInOtherRoom(obj: ConnectableObject, room: Room, p: Point) {
      if (dropPage) {
        switch (obj.className) {
          case "Button": // no itx
            const button = obj as Button;
            const buttonMoved = button.moveToNewParent(room);
            state.selectionManager.selectedObject = button;

            if (buttonMoved) {
              button.channels.forEach((channel) => {
                channel.connections.forEach((connection) => {
                  const connectionMoved = connection.moveToNewParent(dropPage);
                  if (connectionMoved) {
                  } else {
                    logger.error("moveToNewParent failed");
                  }
                });
              });
            }
            break;

          case "Consumer": // no itx
            const consumer = obj as Consumer;
            const consumerMoved = consumer.moveToNewParent(room);
            if (consumerMoved) {
              consumer.connections.forEach((connection) => {
                const connectionMoved = connection.moveToNewParent(dropPage);
                if (connectionMoved) {
                } else {
                  logger.error("moveToNewParent failed");
                }
              });
              //
              state.selectionManager.selectedObject = consumer;
            }
            break;
        }

        const center = new Point(0, 0.5);
        const placementP = new Point(p.x - center.x, p.y - center.y);

        obj.position = placementP;
      }
    }

    try {
      dropPage = page;

      state.selectionManager.selectedObject = undefined;

      const data = JSON.parse(sData);

      var p = eventPointToDrawingArea(e);
      const elm = mainDrawingArea.current;

      if (elm && p) {
        //
        // Place an existing or new Room inside FloorDrawing
        //

        const room = state.objectRegistry.getObject(data.id);

        if (p && mainPage.isFloorPlan && data.className == "Room") {
          if (room && !mainPage.parentFloor?.rooms.asArray().includes(room as Room)) {
            showToast({ text: itx("drawingArea.roomNotInThisFloor", "Der Raum ist nicht auf dieser Etage") });
            return;
          }

          logger.log("drop room", data);
          let obj = state.objectRegistry.getObject(data.id);
          if (!obj) {
            obj = state.objectRegistry.getObject(data.tag);
          }
          if (obj) {
            await mainPage.createRoomInPage(obj as Room, p);
            mainPage.parent?.raiseOnChange("rooms");
            state.eventLogger.addEvent(EventKey.RoomPlacedInFloorPlan, { roomId: (obj as Room).id });
          } else {
            const newRoom = new Room({ tag: "@N", name: "" }, mainPage.parent as Floor); // no itx
            (mainPage.parent as Floor).rooms.add(newRoom);
            await mainPage.createRoomInPage(newRoom, p);

            const ok = await state.dialogManager.openDialog(
              <RoomDialog createNewRoomInFloorDrawing={true} obj={newRoom} parent={mainPage.parent as Floor} />,
            );
            if (ok) {
              state.selectionManager.selectedObject = newRoom;
              state.eventLogger.addEvent(EventKey.RoomPlacedInFloorPlan, { roomId: newRoom.id });
            } else {
              (mainPage.parent as Floor).rooms.remove(newRoom);
            }
          }
          return;
        }
      }

      let room: Room | undefined = undefined;
      if (p) {
        room = mainPage.parent as Room;
      }
      if (isFloorDrawing && p) {
        room = mainPage.getRoomFromPointInBoundingReact(p);

        if (room && !room.isFloorGeneralRoom && room.className == "Room") {
          dropPage = room.getOrCreatePage();
          if (room.roomtoFloorDrawingTransform) {
            p = room.roomtoFloorDrawingTransform.transformPointInvers(p);
          } else {
            showToast({ text: itx("drawingArea.somethingWentWrong", "Upps  ... irgendwas ist schief gelaufen") });
            return;
          }
        } else if (floor) {
          room = floor.getRoomGeneral();
          dropPage = room.getOrCreatePage();
        } else {
          return;
        }
      }

      if (data.className == "Page") {
        const pdfDocument = state.objectRegistry.getObject(data.id) as Page;
        if (pdfDocument.isBuildingPlan) {
          console.log(mainPage.id);
          mainPage.setBackgroundImageFromBuildingPdf(pdfDocument, 1);
        }
        return;
      }

      if (data.className == "PdfPageReference") {
        const pdfPageReference = state.objectRegistry.getObject(data.id) as PdfPageReference;
        const pdfDocument = pdfPageReference.parent as Page;

        if (pdfDocument && pdfDocument.isBuildingPlan) {
          console.log(mainPage.id);
          mainPage.setBackgroundImageFromBuildingPdf(pdfDocument, pdfPageReference.pageNumber);
        }
        return;
      }

      /// adapt point to size of icon
      if (p && room) {
        if (data.className == "DrawingItemTemplate") {
          if (data.category == DrawingItemCategory.Consumer) {
            dropPage.createNewConsumerInPage(room, data, p);
          } else if (data.category == DrawingItemCategory.Button) {
            dropPage.createNewButtonInPage(room, data, p);
          } else if (data.category == DrawingItemCategory.Memo) {
            if (isFloorDrawing && room.isFloorGeneralRoom) {
              mainPage.createNewMemoInPage(room, data, p);
            } else {
              dropPage.createNewMemoInPage(room, data, p);
            }
          }
        } else if (data.className == "DeviceTemplate") {
          // check if it is button, decentralActor or sensor
          const template = state.objectRegistry.getObject(data.id);

          if (template) {
            switch (template.parent?.tag) {
              case "03": // no itx
                data.classification = DeviceClasses.SensorNew;
                break;
              case "07": // no itx
                data.classification = DeviceClasses.DecentralActor;
                break;
            }
          }

          dropPage.createNewButtonInPage(room, data, p);
        } else if (data.className == "Consumer" || data.className == "Button") {
          const obj = state.objectRegistry.getObject(data.id) as BaseModel;
          if (obj.isButton || obj.isConsumer) {
            dropFunctionAllreadyInOtherRoom(obj as ConnectableObject, room, p);
          }
        } else {
          logger.error("not implemented");
        }
      }
    } catch (ex) {
      logger.error("drawing area", ex);
    }
  }

  function onDrop(e: any) {
    logger.log("onDrop");
    var items = e.dataTransfer.items;
    const item = items[0];
    if (item.kind === "string") {
      item.getAsString(function (sData: string) {
        dropIntern(e, sData);
      });
    }
  }

  function onMouseDown(e: any) {}
  function onMouseMove(e: any) {}
  function onMouseUp(e: any) {}
  function onClick(e: any) {}

  function onContextMenu(ev: any) {
    if (page.parent) {
      var p = eventPointToDrawingArea(ev);

      ((page.parent as BaseModel)?.treeViewModel as RoomTreeViewModel).handleAddConsumer(ev, p);
      // state.contextMenuManager.show(ev, page.parent as BaseModel);
    }
  }

  {
    /* <RoomView room={room} /> */
  }

  // const sceneGroupsArray = globalThis.state.sceneGroups.asArray();
  // if (sceneGroupsArray.length > 0) {
  //   sceneGroup = sceneGroupsArray[0];
  // }

  const diagnosticMode = state.settings.diagnosticMode;
  let xConnections = page.connections;

  if (isFloorDrawing && floor) {
    xConnections = floor.getRoomGeneral().getOrCreatePage().connections;
    // logger.log("mwe1: a", xConnections.id);
  }

  let connectionPage = isFloorDrawing ? floor?.getRoomGeneral().getOrCreatePage() : page;
  if (!connectionPage) {
    logger.error("connectionPage undefined");
    connectionPage = page;
  }

  const mainDrawingAreaStyle: any = {
    touchAction: "none",
    pointerEvents: "auto",
    position: "absolute",
    left: "0px",
    top: "0px",
    width: "100%",
    height: "100%",
  };

  if (mainPage.adjustActive.value) {
    return (
      <RDiv
        id="mainDrawingArea"
        ref={mainDrawingArea}
        className="mainDrawingArea"
        style={{ ...mainDrawingAreaStyle, pointerEvents: "none" }}
      />
    );
  }

  const editRooms = page.editRooms;

  const isForWeb = target === RenderTarget.web;

  return (
    <RDiv
      id="mainDrawingArea"
      ref={mainDrawingArea}
      className="mainDrawingArea"
      style={mainDrawingAreaStyle}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDrop={onDrop}
      // onTouchStart={onMouseDown}
      // onTouchMove={onMouseMove}
      // onTouchEnd={onMouseUp}

      // onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      {isForWeb && !mainPage.fixedCabinetView.value && !diagnosticMode && (
        <CabinetView fixed={false} mainPage={mainPage} print={print} adjustActive={page.adjustActive.value} />
      )}

      {isForWeb && !mainPage.fixedCabinetView.value && !diagnosticMode && state.sceneGroup && (
        <RDiv className="sceneGroups">
          <SceneGroupView mainPage={mainPage} sceneGroup={state.sceneGroup} print={print} />
        </RDiv>
      )}

      {isForWeb && !diagnosticMode && !editRooms && (
        <ConnectionsView
          mainPage={mainPage}
          page={connectionPage}
          connections={xConnections}
          print={print}
          fontScale={1}
        />
      )}

      {/* connections view funktioniert zZ nicht innerhalb der ClipViews */}

      <RDiv
        className="clippingArea"
        style={{ pointerEvents: "none", position: "absolute", left: "0px", top: "0px", right: "0px", bottom: "0px" }}
      >
        <RDiv
          className="clippingAreaInner"
          style={{ pointerEvents: "none", position: "absolute", left: "0px", top: "0px", right: "0px", bottom: "0px" }}
        >
          <RoomsView mainPage={mainPage} print={print} />

          {/* Consumer & Buttons */}

          <ConsumersView page={mainPage} mainPage={mainPage} print={print} />

          <MemosView page={mainPage} mainPage={mainPage} print={print} />

          <PageHeader page={mainPage} print={print} />
        </RDiv>
      </RDiv>
    </RDiv>
  );
}
